"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Mail, FileText, MessageSquareText, Clock, CircleCheck } from "lucide-react";
const applicationSteps = [{
  title: "Review the available roles",
  description: "Start with the current opportunities listed above. Each role has its own page with more detail about the work, focus areas, and qualifications.",
  icon: FileText
}, {
  title: "Send your resume and cover letter",
  description: "Applications can be sent by email. A short cover letter is helpful because it gives us context about your experience, interests, and the kind of work you are looking for.",
  icon: Mail
}, {
  title: "We review applications carefully",
  description: "Applications are reviewed as they are received. We look for practical experience, sound judgment, communication skills, and alignment with the kind of client support RiverBend provides.",
  icon: CircleCheck
}, {
  title: "Selected applicants are contacted",
  description: "If there appears to be a good fit, we will contact you to discuss the role, the work environment, expectations, and next steps.",
  icon: MessageSquareText
}];
export default function HeadingDescription() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="252221" id="what-to-expect-when-applying" className="bg-white text-foreground py-16 md:py-24 relative overflow-hidden border-b border-border/40">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-secondary-100/30 rounded-full blur-3xl pointer-events-none" />

      <div ref={ref} className={`max-w-7xl mx-auto px-6 lg:px-8 relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <div className="h-1 w-12 bg-secondary-500 mx-auto mb-6 rounded-full" />

          <p className="text-secondary-700 font-semibold tracking-[0.18em] uppercase text-xs mb-4 font-default">
            Applying to RiverBend
          </p>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-primary-600 mb-6 tracking-tight leading-tight">
            What to expect when applying.
          </h2>

          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-default">
            We want the application process to be clear and respectful. Our
            hiring process is focused on understanding your experience, how you
            approach practical work, and whether the role is a good fit for both
            you and RiverBend.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
          {applicationSteps.map((step, index) => {
          const Icon = step.icon;
          return <div key={index} data-index={index} className="bg-slate-50 border border-border/70 rounded-2xl p-6 md:p-7 shadow-sm h-full">
                <div className="w-12 h-12 rounded-xl bg-secondary-100 flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-secondary-700" />
                </div>

                <h3 className="text-xl font-bold font-default text-primary-600 mb-3 leading-snug">
                  {step.title}
                </h3>

                <p className="text-muted-foreground leading-relaxed font-default text-sm md:text-base">
                  {step.description}
                </p>
              </div>;
        })}
        </div>

        <div className="max-w-4xl mx-auto mt-12 md:mt-16 rounded-3xl border border-primary-100 bg-slate-50 p-6 md:p-8 shadow-sm">
          <div className="flex flex-col md:flex-row gap-5 md:items-start">
            <div className="w-12 h-12 rounded-xl bg-primary-600 text-white flex items-center justify-center shrink-0">
              <Clock className="w-6 h-6" />
            </div>

            <div>
              <h3 className="text-2xl font-bold font-default text-primary-600 mb-3">
                Applications are reviewed on a rolling basis.
              </h3>

              <p className="text-muted-foreground leading-relaxed font-default text-base md:text-lg">
                Unless a job posting states otherwise, roles remain open until
                filled. To apply, send your resume and a brief cover letter to{" "}
                <a href="mailto:employment@riverbendcorp.ca" className="text-secondary-700 font-semibold hover:text-secondary-800 underline underline-offset-4">
                  employment@riverbendcorp.ca
                </a>
                . Please include the role title in the subject line.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}